import React, { useMemo, useState } from "react";
import {
  CheckIcon, ChartIcon, ChevronIcon, CopyIcon, DownloadIcon, MaximizeIcon, SearchIcon, SortIcon, TableIcon,
} from "./Icons";
import {
  cellText, columnsOf, copyText, downloadBlob, formatNumber, prettyHeader, slug, tableToCsv, tableToTsv,
} from "../utils";

const PAGE_SIZES = [10, 25, 50, 100];

function Cell({ value, col }) {
  if (value === null || value === undefined || value === "") return <span className="bdai-cell-empty">—</span>;
  if (typeof value === "boolean") {
    return <span className={`bdai-bool ${value ? "bdai-bool-yes" : "bdai-bool-no"}`}>{value ? "Yes" : "No"}</span>;
  }
  if (typeof value === "number") return <span className="bdai-cell-num">{formatNumber(value, col)}</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="bdai-cell-empty">—</span>;
    if (value.every((v) => typeof v !== "object")) {
      return (
        <span className="bdai-chips">
          {value.slice(0, 4).map((v, i) => (
            <span key={i} className="bdai-chip">{String(v)}</span>
          ))}
          {value.length > 4 && <span className="bdai-chip bdai-chip-more">+{value.length - 4}</span>}
        </span>
      );
    }
  }
  const text = cellText(value);
  return (
    <span className={`bdai-cell-text ${text.length > 28 ? "bdai-cell-long" : ""}`} title={text.length > 60 ? text : undefined}>
      {text}
    </span>
  );
}

function sortValue(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (Array.isArray(v)) return v.length;
  return cellText(v).toLowerCase();
}

/** A table is chartable when it has one text column and at least one numeric column (grouped results). */
function chartInfo(rows, cols) {
  if (rows.length < 2 || rows.length > 60) return null;
  const numeric = cols.filter((c) => rows.every((r) => r[c] === null || r[c] === undefined || typeof r[c] === "number")
    && rows.some((r) => typeof r[c] === "number"));
  const label = cols.find((c) => !numeric.includes(c) && rows.every((r) => typeof r[c] !== "object" || r[c] === null));
  if (!label || !numeric.length) return null;
  return { label, numeric };
}

function BarChart({ rows, label, metric }) {
  const max = Math.max(...rows.map((r) => (typeof r[metric] === "number" ? r[metric] : 0)), 0) || 1;
  return (
    <div className="bdai-chart" role="img" aria-label={`${prettyHeader(metric)} by ${prettyHeader(label)}`}>
      {rows.map((r, i) => {
        const v = typeof r[metric] === "number" ? r[metric] : 0;
        return (
          <div key={i} className="bdai-chart-row">
            <div className="bdai-chart-label" title={cellText(r[label])}>{cellText(r[label]) || "(blank)"}</div>
            <div className="bdai-chart-track">
              <div className="bdai-chart-bar" style={{ width: `${Math.max((v / max) * 100, v ? 1.5 : 0)}%` }} />
            </div>
            <div className="bdai-chart-value">{formatNumber(v, metric)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function DataTable({ title, rows, defaultOpen = true, onExpand, fullView = false }) {
  const cols = useMemo(() => columnsOf(rows), [rows]);
  const chart = useMemo(() => chartInfo(rows, cols), [rows, cols]);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: null, dir: null });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(fullView ? 50 : 10);
  const [view, setView] = useState("table");
  const [metric, setMetric] = useState(chart ? chart.numeric[0] : null);
  const [copied, setCopied] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? rows.filter((r) => cols.some((c) => cellText(r[c]).toLowerCase().includes(q))) : rows;
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const x = sortValue(a[sort.key]);
        const y = sortValue(b[sort.key]);
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        const cmp = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "en", { numeric: true });
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, cols, query, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const visible = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const toggleSort = (key) => {
    setPage(0);
    setSort((s) => (s.key !== key ? { key, dir: "desc" } : s.dir === "desc" ? { key, dir: "asc" } : { key: null, dir: null }));
  };

  const handleCopy = async () => {
    if (await copyText(tableToTsv(filtered))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleCsv = () => {
    downloadBlob(new Blob(["﻿" + tableToCsv(filtered)], { type: "text/csv;charset=utf-8" }), `${slug(title)}.csv`);
  };

  return (
    <div className={`bdai-table-card ${fullView ? "bdai-table-card-full" : ""}`}>
      <div className="bdai-table-head">
        <button
          type="button"
          className="bdai-table-title"
          onClick={() => !fullView && setOpen((o) => !o)}
          aria-expanded={open}
        >
          {!fullView && <ChevronIcon size={14} className={`bdai-table-chevron ${open ? "bdai-open" : ""}`} />}
          <TableIcon size={14} />
          {!fullView && <span className="bdai-table-title-text">{title}</span>}
          <span className="bdai-badge">{fullView ? `${rows.length} rows` : rows.length}</span>
        </button>
        {open && (
          <div className="bdai-table-tools">
            {chart && (
              <div className="bdai-seg" role="tablist" aria-label="View">
                <button type="button" className={`bdai-seg-btn ${view === "table" ? "bdai-active" : ""}`} onClick={() => setView("table")} title="Table view">
                  <TableIcon size={13} />
                </button>
                <button type="button" className={`bdai-seg-btn ${view === "chart" ? "bdai-active" : ""}`} onClick={() => setView("chart")} title="Chart view">
                  <ChartIcon size={13} />
                </button>
              </div>
            )}
            <button type="button" className="bdai-icon-btn bdai-icon-btn-sm" onClick={handleCopy} title="Copy (paste into Excel)">
              {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            </button>
            <button type="button" className="bdai-icon-btn bdai-icon-btn-sm" onClick={handleCsv} title="Download CSV">
              <DownloadIcon size={14} />
            </button>
            {onExpand && !fullView && (
              <button type="button" className="bdai-icon-btn bdai-icon-btn-sm" onClick={onExpand} title="Open full view">
                <MaximizeIcon size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {open && view === "chart" && chart && (
        <div className="bdai-table-body">
          {chart.numeric.length > 1 && (
            <div className="bdai-chart-controls">
              <label className="bdai-muted" htmlFor={`m-${slug(title)}`}>Metric</label>
              <select id={`m-${slug(title)}`} className="bdai-select" value={metric} onChange={(e) => setMetric(e.target.value)}>
                {chart.numeric.map((c) => (
                  <option key={c} value={c}>{prettyHeader(c)}</option>
                ))}
              </select>
            </div>
          )}
          <BarChart rows={filtered} label={chart.label} metric={metric} />
        </div>
      )}

      {open && view === "table" && (
        <div className="bdai-table-body">
          {rows.length > 5 && (
            <div className="bdai-table-search">
              <SearchIcon size={14} />
              <input
                type="search"
                value={query}
                placeholder="Filter rows…"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                aria-label={`Filter ${title}`}
              />
              {query && <span className="bdai-muted">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>}
            </div>
          )}
          <div className="bdai-table-scroll">
            <table className="bdai-table">
              <thead>
                <tr>
                  <th className="bdai-th-index">#</th>
                  {cols.map((c) => (
                    <th key={c} aria-sort={sort.key === c ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                      <button type="button" className="bdai-th-btn" onClick={() => toggleSort(c)}>
                        {prettyHeader(c)}
                        <SortIcon size={11} dir={sort.key === c ? sort.dir : null} className={sort.key === c ? "bdai-sort-on" : "bdai-sort-off"} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={i}>
                    <td className="bdai-th-index">{safePage * pageSize + i + 1}</td>
                    {cols.map((c) => (
                      <td key={c}>
                        <Cell value={r[c]} col={c} />
                      </td>
                    ))}
                  </tr>
                ))}
                {!visible.length && (
                  <tr>
                    <td colSpan={cols.length + 1} className="bdai-table-empty">No rows match “{query}”.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > PAGE_SIZES[0] && (
            <div className="bdai-pager">
              <span className="bdai-muted">
                {safePage * pageSize + 1}–{Math.min(filtered.length, (safePage + 1) * pageSize)} of {filtered.length}
              </span>
              <div className="bdai-pager-right">
                <select
                  className="bdai-select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  aria-label="Rows per page"
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
                <button type="button" className="bdai-pager-btn" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} aria-label="Previous page">‹</button>
                <span className="bdai-muted">{safePage + 1} / {pages}</span>
                <button type="button" className="bdai-pager-btn" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} aria-label="Next page">›</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
